import React, { useState } from 'react';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { useT } from '@/contexts/I18nContext';

import { type LongQueueFields, type NoVisitorsFields, serializeLongQueue, serializeNoVisitors } from './treeBlock';


/**
 * Edits the "Очередь от N человек: ..." line (checkout_control.py's
 * `long_queue_line`). Small enough to sit alongside `NoVisitorsForm` in one
 * file rather than its own — both are flat single-line forms, unlike the
 * multi-branch CategoryTreeForm (the "Самая длинная очередь" block is edited
 * in place by InlineQueueRows' InlineLongestQueueRow).
 */
export function LongQueueForm({
  initial,
  onApply,
  onCancel,
}: {
  initial: LongQueueFields;
  onApply: (row: string) => void;
  onCancel: () => void;
}) {
  const { t } = useT();
  const [f, setF] = useState<LongQueueFields>(initial);
  const set = (patch: Partial<LongQueueFields>) => setF((prev) => ({ ...prev, ...patch }));
  const preview = serializeLongQueue(f);

  return (
    <div className="flex flex-col gap-4">
      <div className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
        {/* The DERIVED circle (first token of the preview), not the parsed one —
            the two differ on any row whose stored colour predates the
            always-derived rule. */}
        {preview.split(' ')[0]} {t('reportValidation.tf.q_longqueue')}
      </div>

      {/* Colour derived from the parameters (Alexey 2026-09-08) — no picker. */}

      <label className="flex items-center gap-2 text-sm">
        <input
          type="checkbox"
          checked={f.none}
          onChange={(e) => set({ none: e.target.checked })}
          className="size-4"
        />
        <span className="text-muted-foreground">{t('reportValidation.tree.queueNone')}</span>
      </label>

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <label className="flex flex-col gap-1 text-sm">
          <span className="text-muted-foreground">{t('reportValidation.tf.threshold')}</span>
          <Input
            type="number"
            min={0}
            value={f.threshold}
            onChange={(e) => set({ threshold: Math.max(0, Number(e.target.value) || 0) })}
          />
        </label>
        {!f.none && (
          <label className="flex flex-col gap-1 text-sm">
            <span className="text-muted-foreground">{t('reportValidation.tree.queueMinutes')}</span>
            <Input
              type="number"
              min={0}
              value={f.minutes}
              onChange={(e) => set({ minutes: Math.max(0, Number(e.target.value) || 0) })}
            />
          </label>
        )}
      </div>

      {!f.none && (
        <div className="flex flex-col gap-2 text-sm">
          <label className="flex items-center gap-2">
            <input
              type="checkbox"
              checked={f.secondCashierOn}
              onChange={(e) => set({ secondCashierOn: e.target.checked })}
              className="size-4"
            />
            <span className="text-muted-foreground">{t('reportValidation.tf.secondCashierOn')}</span>
          </label>
          {f.secondCashierOn && (
            <div className="flex flex-wrap gap-3">
              <label className="flex flex-col gap-1">
                <span className="text-muted-foreground">{t('reportValidation.tf.secondCashierMinutes')}</span>
                <Input
                  type="number"
                  min={0}
                  value={f.secondCashierMinutes}
                  onChange={(e) => set({ secondCashierMinutes: Math.max(0, Number(e.target.value) || 0) })}
                  className="w-32"
                />
              </label>
            </div>
          )}
        </div>
      )}

      <div className="flex flex-col gap-1">
        <span className="text-xs text-muted-foreground">{t('reportValidation.form.preview')}</span>
        <div className="report-body rounded-md border border-border bg-muted/40 p-2 text-sm" dangerouslySetInnerHTML={{ __html: preview }} />
      </div>

      <div className="flex justify-end gap-2">
        <Button type="button" variant="ghost" size="sm" onClick={onCancel}>
          {t('reportValidation.form.cancel')}
        </Button>
        <Button type="button" size="sm" onClick={() => onApply(preview)}>
          {t('reportValidation.form.apply')}
        </Button>
      </div>
    </div>
  );
}

/** Edits the "Без посетителей у кассы: N часов из M" line — always ⚪,
 *  no colour/underline (see checkout_control.py's comment on why that
 *  metric is deliberately left un-thresholded). */
export function NoVisitorsForm({
  initial,
  onApply,
  onCancel,
  hidePreview,
}: {
  initial: NoVisitorsFields;
  onApply: (row: string) => void;
  onCancel: () => void;
  hidePreview?: boolean;
}) {
  const { t } = useT();
  const [f, setF] = useState<NoVisitorsFields>(initial);
  const preview = serializeNoVisitors(f);

  return (
    <div className="flex flex-col gap-4">
      <div className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
        ⚪ {t('reportValidation.tf.q_novisit')}
      </div>

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <label className="flex flex-col gap-1 text-sm">
          <span className="text-muted-foreground">{t('reportValidation.tf.hoursNoVisit')}</span>
          <Input
            type="number"
            min={0}
            value={f.zeroHours}
            onChange={(e) => setF({ ...f, zeroHours: Math.max(0, Number(e.target.value) || 0) })}
          />
        </label>
        <label className="flex flex-col gap-1 text-sm">
          <span className="text-muted-foreground">{t('reportValidation.tf.totalHours')}</span>
          <Input
            type="number"
            min={0}
            value={f.totalHours}
            onChange={(e) => setF({ ...f, totalHours: Math.max(0, Number(e.target.value) || 0) })}
          />
        </label>
      </div>

      {!hidePreview && (
        <div className="flex flex-col gap-1">
          <span className="text-xs text-muted-foreground">{t('reportValidation.form.preview')}</span>
          <div className="report-body rounded-md border border-border bg-muted/40 p-2 text-sm">{preview}</div>
        </div>
      )}

      <div className="flex justify-end gap-2">
        <Button type="button" variant="ghost" size="sm" onClick={onCancel}>
          {t('reportValidation.form.cancel')}
        </Button>
        <Button type="button" size="sm" onClick={() => onApply(preview)}>
          {t('reportValidation.form.apply')}
        </Button>
      </div>
    </div>
  );
}
